import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Container, Card, Badge, Button, Spinner, Alert, Form, ListGroup } from 'react-bootstrap';
import { useLazyGetMeetingsAgendaQuery, useBuildMeetingBriefMutation } from '../app/api';
import { msalInstance, graphMailRequest } from '../msalConfig';
import { useIsMobile } from './utils';

// Meetings Assistant (beta).
//
// Left: the next week of meetings from the employee's own calendar. Right: the
// selected meeting, its attendees resolved against our CRM, and an AI prep brief
// built from mail history with those people.
//
// On a phone those two panes become ONE pane that swaps — agenda, then the
// meeting, with a back button. Side by side collapses to stacked, which put the
// thing you just tapped a scroll-and-a-half below the fold; a swap keeps the
// answer where the tap was. Everything above 768px is unchanged.
//
// The delegated Graph token is acquired here and passed per request — the hub
// never holds a token that can read mail. Same pattern as Your Day, including the
// interactive-sign-in fallback: a restored tab often has a valid hub session and
// no MSAL account, and the interactive call has to come from a user gesture, so a
// silent miss surfaces a button rather than retrying invisibly.

const brand = { deepBlue: '#0a2947', tealBlue: '#0069a6', goldenrod: '#d09b2c', charcoal: '#58585b' };

// Minimum comfortable tap target, matching Your Day.
const TAP = 44;

function when(iso) {
  if (!iso) return '';
  const d = new Date(iso.endsWith('Z') ? iso : `${iso}Z`);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString(undefined, {
    weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  });
}

function dayKey(iso) {
  if (!iso) return '';
  const d = new Date(iso.endsWith('Z') ? iso : `${iso}Z`);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString(undefined, {
    weekday: 'long', month: 'long', day: 'numeric',
  });
}

/** Attendee chip: client / colleague / unknown, with the CRM household when we have one.
 *
 * The whole row is the control on a phone: a 16px checkbox is a miss waiting to
 * happen, and picking who to brief on is the one thing this page asks you to do.
 * The label wraps the content rather than an onClick on the row, so the native
 * checkbox keeps its keyboard and screen-reader behaviour for free. */
function Attendee({ person, checked, onToggle, isMobile }) {
  const kindColor = person.kind === 'client' ? brand.tealBlue
    : person.kind === 'employee' ? brand.charcoal : brand.goldenrod;
  return (
    <ListGroup.Item
      as="label"
      htmlFor={`att-${person.email}`}
      className="d-flex align-items-start gap-2 py-2 mb-0"
      style={{ minHeight: isMobile ? TAP : 'auto', cursor: 'pointer' }}
    >
      <Form.Check
        type="checkbox"
        checked={checked}
        onChange={() => onToggle(person.email)}
        id={`att-${person.email}`}
        className="mt-1"
        style={isMobile ? { transform: 'scale(1.2)' } : undefined}
      />
      <div style={{ minWidth: 0 }}>
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <strong style={{ fontSize: '0.9rem' }}>{person.name || person.display_name || person.email}</strong>
          <Badge bg="" style={{ background: kindColor, fontSize: '0.65rem' }}>
            {person.kind === 'employee' ? 'colleague' : person.kind}
          </Badge>
          {person.has_portal_login && <Badge bg="light" text="dark" style={{ fontSize: '0.62rem' }}>portal</Badge>}
        </div>
        <div className="text-muted" style={{ fontSize: '0.75rem', wordBreak: 'break-all' }}>{person.email}</div>
        {person.household_name && (
          <div style={{ fontSize: '0.75rem' }}>
            {person.household_name}
            {person.rep_name ? ` · ${person.rep_name}` : ''}
            {person.rep_firm_name ? ` · ${person.rep_firm_name}` : ''}
          </div>
        )}
      </div>
    </ListGroup.Item>
  );
}

Attendee.propTypes = {
  person: PropTypes.object.isRequired,
  checked: PropTypes.bool,
  onToggle: PropTypes.func.isRequired,
  isMobile: PropTypes.bool,
};

/** The structured brief. Renders sections when present, falling back to markdown text. */
function Brief({ data }) {
  const s = data?.sections;
  if (!s) return <div style={{ whiteSpace: 'pre-wrap' }}>{data?.brief}</div>;
  const section = (title, items, render) => {
    if (!items || items.length === 0) return null;
    return (
      <div className="mb-3">
        <div style={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.5px',
                      textTransform: 'uppercase', color: brand.charcoal }}>{title}</div>
        <ul className="mb-0 ps-3" style={{ fontSize: '0.88rem' }}>
          {items.map((it, i) => <li key={i}>{render ? render(it) : it}</li>)}
        </ul>
      </div>
    );
  };
  return (
    <>
      {s.headline && <p style={{ fontSize: '0.95rem', fontWeight: 600 }}>{s.headline}</p>}
      {section("Who you're meeting", s.who, (w) => <><strong>{w.name}</strong> — {w.context}</>)}
      {section('Recent history', s.recent_history)}
      {section('Open items', s.open_items)}
      {section('Talking points', s.talking_points)}
      {s.watch_outs?.length > 0 && (
        <Alert variant="warning" className="py-2 mb-0" style={{ fontSize: '0.85rem' }}>
          <strong>Watch out: </strong>
          {s.watch_outs.join(' · ')}
        </Alert>
      )}
    </>
  );
}

Brief.propTypes = { data: PropTypes.object };

export default function Meetings() {
  const [fetchAgenda, { data, isFetching, error }] = useLazyGetMeetingsAgendaQuery();
  const [buildBrief, { isLoading: building }] = useBuildMeetingBriefMutation();
  const isMobile = useIsMobile();

  const [needsSignIn, setNeedsSignIn] = useState(false);
  const [selected, setSelected] = useState(null);       // the chosen meeting
  // Which of the two panes a PHONE is showing. Ignored above 768px, where both
  // are on screen at once and there is nothing to swap.
  const [showDetail, setShowDetail] = useState(false);
  const [picked, setPicked] = useState([]);             // emails to brief on
  const [brief, setBrief] = useState(null);
  const [depth, setDepth] = useState('quick');
  const [failure, setFailure] = useState('');

  const account = () => msalInstance.getAllAccounts?.()[0] || null;

  const token = useCallback(async ({ interactive = false } = {}) => {
    const acct = account();
    if (!acct && !interactive) { setNeedsSignIn(true); return null; }
    try {
      const req = acct ? { ...graphMailRequest, account: acct } : graphMailRequest;
      const res = interactive
        ? await msalInstance.acquireTokenPopup(req)
        : await msalInstance.acquireTokenSilent(req);
      setNeedsSignIn(false);
      return res.accessToken;
    } catch (e) {
      setNeedsSignIn(true);
      return null;
    }
  }, []);

  const loadAgenda = useCallback(async (opts) => {
    const t = await token(opts);
    if (t) fetchAgenda({ graphToken: t, days: 7 });
  }, [token, fetchAgenda]);

  useEffect(() => { loadAgenda(); }, [loadAgenda]);

  const meetings = data?.meetings || [];
  const grouped = useMemo(() => {
    const out = [];
    meetings.forEach((m) => {
      const key = dayKey(m.start);
      const last = out[out.length - 1];
      if (last && last.day === key) last.items.push(m);
      else out.push({ day: key, items: [m] });
    });
    return out;
  }, [meetings]);

  const choose = (m) => {
    setShowDetail(true);
    // The detail pane REPLACES the agenda on a phone, so it has to start at its
    // own top — otherwise a tap near the bottom of a long agenda opens the
    // meeting already scrolled past its heading.
    if (isMobile) window.scrollTo({ top: 0 });
    // Re-opening the meeting you were just on is a navigation, not a reset. On
    // mobile that happens every time you go back to the agenda and return, and
    // discarding a brief they already waited for would be its own bug.
    if (m.id === selected?.id) return;
    setSelected(m);
    setBrief(null);
    setFailure('');
    // Pre-select the external attendees: briefing an advisor on their own
    // colleagues is rarely what they wanted.
    setPicked((m.attendees || []).filter(a => !a.internal).map(a => a.email));
  };

  // Back to the agenda, mobile only. The SELECTION survives deliberately — the
  // brief comes back with it, and it keeps the row you came from highlighted.
  const backToAgenda = () => {
    setShowDetail(false);
    window.scrollTo({ top: 0 });
  };

  const toggle = (email) => setPicked(p => p.includes(email) ? p.filter(e => e !== email) : [...p, email]);

  const rampUp = async ({ refresh = false } = {}) => {
    setFailure('');
    const t = await token();
    if (!t || !selected) return;
    try {
      const res = await buildBrief({
        graphToken: t, eventId: selected.id, emails: picked, depth, refresh,
      }).unwrap();
      setBrief(res);
    } catch (e) {
      setFailure(e?.data?.detail || 'Could not build the brief.');
    }
  };

  if (needsSignIn) {
    return (
      <Container className="py-4">
        <h3>Meetings <Badge bg="warning" text="dark">Beta</Badge></h3>
        <Alert variant="info" className="mt-3">
          <p className="mb-2">Connect your Microsoft account so this page can read your calendar and mail history.</p>
          <Button onClick={() => token({ interactive: true }).then(t => t && loadAgenda())}>Connect</Button>
        </Alert>
      </Container>
    );
  }

  return (
    <Container className="py-4">
      <div className="d-flex align-items-center gap-2 mb-1">
        <h3 className="mb-0">Meetings</h3>
        <Badge bg="warning" text="dark">Beta</Badge>
      </div>
      {/* Instructions for a thing you've already done are just something to
          scroll past, and a phone has the least room to spare. */}
      {!(isMobile && showDetail) && (
        <p className="text-muted" style={{ maxWidth: '46rem', fontSize: '0.9rem' }}>
          Your next seven days. Pick a meeting, choose who you want to be briefed on,
          and get a prep brief built from your history with them
        </p>
      )}

      {error && <Alert variant="danger">{error?.data?.detail || 'Could not load your calendar.'}</Alert>}

      <div className="d-flex gap-3 flex-wrap align-items-start">
        {/* Agenda. On a phone this is the whole screen until a meeting is picked,
            and it comes back when they press Back. */}
        {(!isMobile || !showDetail) && (
        <Card style={{ flex: '1 1 340px', minWidth: isMobile ? 0 : '320px', width: isMobile ? '100%' : undefined }}>
          <Card.Header className="d-flex justify-content-between align-items-center">
            <span style={{ fontWeight: 700, fontSize: '0.8rem', textTransform: 'uppercase',
                           letterSpacing: '0.5px', color: brand.deepBlue }}>Next 7 days</span>
            <Button size="sm" variant="outline-secondary" onClick={() => loadAgenda()} disabled={isFetching}
                    style={isMobile ? { minWidth: TAP, minHeight: TAP } : undefined}
                    aria-label="Refresh agenda">
              {isFetching ? <Spinner size="sm" animation="border" /> : <i className="bi bi-arrow-repeat" />}
            </Button>
          </Card.Header>
          {isFetching && meetings.length === 0 ? (
            <Card.Body className="text-center py-4"><Spinner animation="border" /></Card.Body>
          ) : meetings.length === 0 ? (
            <Card.Body className="text-muted text-center py-4" style={{ fontSize: '0.9rem' }}>
              No meetings with other attendees in the next 7 days.
            </Card.Body>
          ) : (
            <ListGroup variant="flush">
              {grouped.map(g => (
                <React.Fragment key={g.day}>
                  <ListGroup.Item className="py-1" style={{ background: 'rgba(10,41,71,0.04)',
                    fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    {g.day}
                  </ListGroup.Item>
                  {g.items.map(m => (
                    <ListGroup.Item
                      key={m.id} action active={selected?.id === m.id} onClick={() => choose(m)}
                      className="py-2"
                      style={isMobile ? { minHeight: TAP } : undefined}
                    >
                      <div className="d-flex justify-content-between align-items-start gap-2">
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontWeight: 600, fontSize: '0.88rem' }}>{m.subject}</div>
                          <div style={{ fontSize: '0.75rem', opacity: 0.75 }}>
                            {when(m.start)} · {m.attendees.length} attendee{m.attendees.length === 1 ? '' : 's'}
                          </div>
                        </div>
                        <div className="d-flex flex-column align-items-end gap-1">
                          {m.has_client && <Badge bg="" style={{ background: brand.tealBlue, fontSize: '0.6rem' }}>client</Badge>}
                          {m.has_brief && <Badge bg="success" style={{ fontSize: '0.6rem' }}>briefed</Badge>}
                          {/* A chevron only earns its place where the row leads
                              somewhere else, which on desktop it doesn't. */}
                          {isMobile && <i className="bi bi-chevron-right" style={{ fontSize: '0.7rem', opacity: 0.5 }} />}
                        </div>
                      </div>
                    </ListGroup.Item>
                  ))}
                </React.Fragment>
              ))}
            </ListGroup>
          )}
        </Card>
        )}

        {/* Selected meeting + brief. The empty "pick a meeting" state is desktop
            only — on a phone this pane isn't on screen until they've picked one,
            so an instruction to do the thing they just did would be noise. */}
        {(!isMobile || showDetail) && (
        <Card style={{ flex: '2 1 460px', minWidth: isMobile ? 0 : '340px', width: isMobile ? '100%' : undefined }}>
          {!selected ? (
            <Card.Body className="text-muted text-center py-5" style={{ fontSize: '0.9rem' }}>
              <i className="bi bi-calendar-check d-block mb-2" style={{ fontSize: '1.6rem', opacity: 0.4 }} />
              Pick a meeting to prepare for.
            </Card.Body>
          ) : (
            <>
              <Card.Header>
                {isMobile && (
                  <Button
                    variant="link" size="sm" onClick={backToAgenda}
                    className="px-0 mb-1 d-flex align-items-center gap-1 text-decoration-none"
                    style={{ minHeight: TAP, fontSize: '0.82rem', fontWeight: 600 }}
                  >
                    <i className="bi bi-chevron-left" /> Agenda
                  </Button>
                )}
                <div style={{ fontWeight: 700, color: brand.deepBlue }}>{selected.subject}</div>
                <div className="text-muted" style={{ fontSize: '0.78rem' }}>
                  {when(selected.start)}{selected.location ? ` · ${selected.location}` : ''}
                </div>
              </Card.Header>
              <Card.Body>
                <div style={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.5px',
                              textTransform: 'uppercase', color: brand.charcoal }}>
                  Brief me on 
                </div>
                <ListGroup variant="flush" className="mb-3">
                  {selected.attendees.map(a => (
                    <Attendee key={a.email} person={a} checked={picked.includes(a.email)}
                              onToggle={toggle} isMobile={isMobile} />
                  ))}
                </ListGroup>

                {/* Stacked and full-width on a phone: three controls sharing a
                    wrapped row put "Create Brief" in a different place depending
                    on whether Regenerate is showing. */}
                <div className={`d-flex gap-2 mb-3 ${isMobile
                  ? 'flex-column align-items-stretch' : 'align-items-center flex-wrap'}`}>
                  <Form.Select size={isMobile ? undefined : 'sm'} value={depth}
                               style={{ width: isMobile ? '100%' : 'auto',
                                        minHeight: isMobile ? TAP : undefined }}
                               onChange={e => setDepth(e.target.value)}>
                    <option value="quick">Quick brief</option>
                    <option value="deep">Deep brief</option>
                  </Form.Select>
                  <Button size={isMobile ? undefined : 'sm'} onClick={() => rampUp()}
                          disabled={building || picked.length === 0}
                          style={isMobile ? { minHeight: TAP } : undefined}>
                    {building ? <><Spinner size="sm" animation="border" /> Building…</> : 'Create Brief'}
                  </Button>
                  {brief && (
                    <Button size={isMobile ? undefined : 'sm'} variant="outline-secondary"
                            onClick={() => rampUp({ refresh: true })} disabled={building}
                            style={isMobile ? { minHeight: TAP } : undefined}>
                      Regenerate
                    </Button>
                  )}
                  {picked.length === 0 && (
                    <span className="text-muted" style={{ fontSize: '0.78rem' }}>Pick at least one person.</span>
                  )}
                </div>

                {failure && <Alert variant="danger" className="py-2" style={{ fontSize: '0.85rem' }}>{failure}</Alert>}

                {brief && (
                  <>
                    <hr />
                    <Brief data={brief} />
                    {/* Provenance, not content: how much was read, and how old it is. */}
                    <div className="text-muted mt-3" style={{ fontSize: '0.72rem' }}>
                      Built from {brief.message_count} email(s)
                      {brief.recap_count ? ` and ${brief.recap_count} meeting recap(s)` : ''}
                      {brief.lookback_days ? ` over the last ${Math.round(brief.lookback_days / 365)} year(s)` : ''}
                      {brief.cached ? ' · showing a saved brief — press Regenerate for a fresh one' : ''}
                    </div>
                  </>
                )}
              </Card.Body>
            </>
          )}
        </Card>
        )}
      </div>
    </Container>
  );
}
